import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { xosoService } from "@/services/xosoService";
import useXosoLive, { selectLiveResults } from "../XoSoCompound/useXosoLive";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const DAYS = 30;
const TOP = 10;

const REGION_CODE = { mb: "MB", mn: "MN", mt: "MT" };
const REGION_LABEL = { mb: "Miền Bắc", mn: "Miền Nam", mt: "Miền Trung" };

const WEEKDAYS = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];

const PRIZE_ROWS = {
  MB: [
    { key: "specialPrize", label: "ĐB", cls: "kbwxs-gdb" },
    { key: "prize1", label: "G.1", cls: "kbwxs-g1" },
    { key: "prize2", label: "G.2", cls: "kbwxs-g2" },
    { key: "prize3", label: "G.3", cls: "kbwxs-g3" },
    { key: "prize4", label: "G.4", cls: "kbwxs-g4" },
    { key: "prize5", label: "G.5", cls: "kbwxs-g5" },
    { key: "prize6", label: "G.6", cls: "kbwxs-g6" },
    { key: "prize7", label: "G.7", cls: "kbwxs-g7" },
  ],
  MN: [
    { key: "prize8", label: "G.8", cls: "kbwxs-g8" },
    { key: "prize7", label: "G.7", cls: "kbwxs-g7" },
    { key: "prize6", label: "G.6", cls: "kbwxs-g6" },
    { key: "prize5", label: "G.5", cls: "kbwxs-g5" },
    { key: "prize4", label: "G.4", cls: "kbwxs-g4" },
    { key: "prize3", label: "G.3", cls: "kbwxs-g3" },
    { key: "prize2", label: "G.2", cls: "kbwxs-g2" },
    { key: "prize1", label: "G.1", cls: "kbwxs-g1" },
    { key: "specialPrize", label: "ĐB", cls: "kbwxs-gdb" },
  ],
};

PRIZE_ROWS.MT = PRIZE_ROWS.MN;

const FETCHERS = {
  MB: (params) => xosoService.getXsmb(params),
  MN: (params) => xosoService.getXsmn(params),
  MT: (params) => xosoService.getXsmt(params),
};

const statsCache = new Map();

function todayStr() {
  return dayjs().tz(TZ).format("YYYY-MM-DD");
}

function drawDateKey(drawDate) {
  if (!drawDate) return null;
  const d = dayjs.utc(drawDate);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

function formatPaneDate(drawDate) {
  const key = drawDateKey(drawDate);
  if (!key) return "";
  const d = dayjs.utc(key);
  return `${WEEKDAYS[d.day()]}, ${d.format("DD/MM/YYYY")}`;
}

function lotoBuckets(prizes) {
  const buckets = Array.from({ length: 10 }, () => []);
  Object.values(prizes || {}).forEach((nums) => {
    if (!Array.isArray(nums)) return;
    nums.forEach((num) => {
      const digits = String(num).replace(/\D/g, "");
      if (digits.length < 2) return;
      const tail = digits.slice(-2);
      buckets[Number(tail[0])].push(tail);
    });
  });
  buckets.forEach((list) => list.sort());
  return buckets;
}

function splitSpecial(num) {
  const value = String(num ?? "").trim();
  if (!/^\d{3,}$/.test(value)) return null;
  return { prefix: value.slice(0, -2), tail: value.slice(-2) };
}

function readDigits(map) {
  return Array.from({ length: 10 }, (_, index) => Number(map?.[index] ?? map?.[String(index)] ?? 0));
}

function aggregateFrequency(packs) {
  const totals = new Map();
  packs.forEach(({ frequency }) => {
    (frequency || []).forEach(({ pair, count }) => {
      totals.set(pair, (totals.get(pair) || 0) + Number(count || 0));
    });
  });
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .slice(0, TOP)
    .map(([pair, count]) => ({ num: pair, label: `${count} lần` }));
}

function aggregateGan(packs) {
  const totals = new Map();
  packs.forEach(({ gan }) => {
    (gan || []).forEach(({ pair, daysSinceLastSeen }) => {
      const prev = totals.get(pair);
      const next = daysSinceLastSeen == null ? null : Number(daysSinceLastSeen);
      if (prev === undefined) {
        totals.set(pair, next);
        return;
      }
      if (next == null) return;
      if (prev == null || next < prev) totals.set(pair, next);
    });
  });
  return [...totals.entries()]
    .sort((a, b) => {
      const da = a[1] == null ? Number.POSITIVE_INFINITY : a[1];
      const db = b[1] == null ? Number.POSITIVE_INFINITY : b[1];
      if (db !== da) return db - da;
      return a[0] < b[0] ? -1 : 1;
    })
    .slice(0, TOP)
    .map(([pair, days]) => ({ num: pair, label: `${days == null ? DAYS : days} kỳ` }));
}

function aggregateHeadTail(packs) {
  const heads = Array(10).fill(0);
  const tails = Array(10).fill(0);
  packs.forEach(({ headTail }) => {
    readDigits(headTail?.heads).forEach((count, index) => {
      heads[index] += count;
    });
    readDigits(headTail?.tails).forEach((count, index) => {
      tails[index] += count;
    });
  });
  return {
    heads: heads.map(String),
    tails: tails.map(String),
  };
}

async function loadSpecialHistory(region) {
  const today = dayjs().tz(TZ).startOf("day");
  const dates = Array.from({ length: DAYS }, (_, index) => today.subtract(index, "day").format("YYYY-MM-DD"));
  const batches = await Promise.all(dates.map((date) => FETCHERS[region]({ date })));
  const byDate = new Map();

  batches.flat().forEach((item) => {
    const key = drawDateKey(item?.drawDate);
    const special = splitSpecial(item?.prizes?.specialPrize?.[0]);
    if (!key || !special) return;
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key).push({
      name: item.province?.name || "",
      prefix: special.prefix,
      tail: special.tail,
    });
  });

  const ordered = [...byDate.keys()].sort((a, b) => (a < b ? 1 : -1));
  if (region === "MB") {
    return {
      dbgrid: ordered.map((key) => {
        const station = byDate.get(key)[0];
        return {
          date: dayjs.utc(key).format("DD/MM"),
          prefix: station.prefix,
          tail: station.tail,
        };
      }),
      dbh: [],
    };
  }

  return {
    dbgrid: [],
    dbh: ordered.map((key) => ({
      date: dayjs.utc(key).format("DD/MM"),
      stations: byDate.get(key),
    })),
  };
}

async function fetchStats(region) {
  const provinces = await xosoService.getProvinces(region);
  const limit = provinces.length > 1 ? 100 : TOP;
  const [packs, history] = await Promise.all([
    Promise.all(
      provinces.map(async (province) => {
        const params = { region, province: province.code, days: DAYS };
        const [frequency, gan, headTail] = await Promise.all([
          xosoService.getLotoFrequency({ ...params, limit }),
          xosoService.getLotoGan({ ...params, limit }),
          xosoService.getHeadTailStats(params),
        ]);
        return { frequency, gan, headTail };
      })
    ),
    loadSpecialHistory(region),
  ]);

  const headTail = aggregateHeadTail(packs);
  return {
    cards: [
      { icon: "fire", title: "Lô ra nhiều nhất", chips: aggregateFrequency(packs) },
      { icon: "ice", title: "Lô gan (lâu chưa về)", chips: aggregateGan(packs) },
    ],
    freq: [
      { label: "Đầu", cells: headTail.heads },
      { label: "Đuôi", cells: headTail.tails },
    ],
    dbgrid: history.dbgrid,
    dbh: history.dbh,
  };
}

function loadStats(region) {
  if (!statsCache.has(region)) {
    const job = fetchStats(region).catch((error) => {
      statsCache.delete(region);
      console.error(error);
      return null;
    });
    statsCache.set(region, job);
  }
  return statsCache.get(region);
}

function buildBoard(region, results) {
  const rows = PRIZE_ROWS[region];
  const columns = results.length ? results : [{}];
  const multi = region !== "MB" && results.length > 0;
  const names = results.map((item) => item.province?.name || "");
  const buckets = columns.map((item) => lotoBuckets(item.prizes));

  return {
    date: formatPaneDate(results[0]?.drawDate),
    kqHeads: multi ? ["Giải", ...names] : [],
    kq: rows.map((row) => ({
      cls: row.cls,
      label: row.label,
      cols: columns.map((item) => (Array.isArray(item.prizes?.[row.key]) ? item.prizes[row.key] : [])),
    })),
    lotoHeads: multi ? ["Đầu", ...names] : ["Đầu", "Lô tô"],
    loto: Array.from({ length: 10 }, (_, head) => ({
      head: String(head),
      cols: buckets.map((bucket) => bucket[head]),
    })),
  };
}

const EMPTY_STATS = {
  cards: [
    { icon: "fire", title: "Lô ra nhiều nhất", chips: [] },
    { icon: "ice", title: "Lô gan (lâu chưa về)", chips: [] },
  ],
  freq: [
    { label: "Đầu", cells: Array(10).fill("") },
    { label: "Đuôi", cells: Array(10).fill("") },
  ],
  dbgrid: [],
  dbh: [],
};

export default function useTruyenThongPane(regionId, statsOpen) {
  const region = REGION_CODE[regionId] || "MB";
  const [pack, setPack] = useState({ region: null, results: [] });
  const [statsPack, setStatsPack] = useState({ region: null, data: null });
  const live = useXosoLive(region, true);
  const rest = pack.region === region ? pack.results : [];
  const merged = useMemo(() => selectLiveResults(rest, live), [rest, live]);

  useEffect(() => {
    let cancelled = false;
    const requested = region;

    (async () => {
      const today = todayStr();
      let data = await FETCHERS[requested]({ date: today });
      if (!data.length) data = await FETCHERS[requested]({});
      if (!cancelled) setPack({ region: requested, results: data });
    })();

    return () => {
      cancelled = true;
    };
  }, [region]);

  useEffect(() => {
    if (!statsOpen) return undefined;
    let cancelled = false;
    const requested = region;

    (async () => {
      const data = await loadStats(requested);
      if (!cancelled && data) setStatsPack({ region: requested, data });
    })();

    return () => {
      cancelled = true;
    };
  }, [region, statsOpen]);

  return useMemo(() => {
    const board = buildBoard(region, merged);
    const stats = statsPack.region === region && statsPack.data ? statsPack.data : EMPTY_STATS;
    return {
      ...board,
      statHeads: [`Thống kê ${DAYS} ngày · ${REGION_LABEL[regionId] || REGION_LABEL.mb}`, "Lịch sử giải đặc biệt"],
      cards: stats.cards,
      freq: stats.freq,
      dbgrid: stats.dbgrid,
      dbh: stats.dbh,
    };
  }, [region, regionId, merged, statsPack]);
}
